import React, { useRef, useCallback } from "react";
import classNames from "classnames";
import minus from "@/assets/minusq.svg";
import plus from "@/assets/plusq.svg";
import { t } from "i18next";
import styles from "./index.module.scss";
import { Input } from "@headlessui/react";
import { FixButton } from "@/elements-react/fix-button";
import { Icon } from "@/elements-react/fix-icon";
import { useLayoutType, LayoutStyleTypes, LayoutTypes } from "@/util-react/layout";

type FixQuantityProps = {
  type?: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM;
  quantity?: number;
  minQuantity?: number;
  maxQuantity?: number | null;
  factorQuantity?: number | null;
  disabled?: boolean;
  variants?: "warning" | "regular";
  onUpdateQuantity?: (quantity: number) => void;
};

const DEFAULT_MAX = 999999;

export const FixQuantity: React.FC<FixQuantityProps> = ({
  type = LayoutStyleTypes.TYPE_LG,
  quantity = 1,
  minQuantity = 0,
  maxQuantity = null,
  factorQuantity = null,
  disabled = false,
  variants = "regular",
  onUpdateQuantity,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const normalizeQuantity = useCallback(
    (value: number) => {
      let result = value;
      if (factorQuantity) {
        result = Math.max(minQuantity, Math.ceil(result / factorQuantity) * factorQuantity);
      }
      if (maxQuantity === null) {
        return Math.min(Math.max(result, minQuantity), DEFAULT_MAX);
      }
      return Math.min(Math.max(result, minQuantity), maxQuantity);
    },
    [factorQuantity, minQuantity, maxQuantity],
  );

  const validateQuantity = useCallback(
    (value: number): string | null => {
      if (factorQuantity && value % factorQuantity !== 0) {
        return t("ssmaa79d8gh21mmd.value-multiple", "Value should be multiple of {{quantity}}", { quantity: factorQuantity });
      }
      if (maxQuantity && value > maxQuantity) {
        return t("18fssp482n3n2mfs.greater-then", "Value greater than {{max}} not allowed", { max: maxQuantity });
      }
      if (value < minQuantity) {
        return t("flw8d74masslsi48v.less-then", "Value less than {{min}} not allowed", { min: minQuantity });
      }
      return null;
    },
    [factorQuantity, maxQuantity, minQuantity],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    const norm = normalizeQuantity(val);
    if (onUpdateQuantity) onUpdateQuantity(norm);

    handleInputUpdate(val);
  };

  const handleInputUpdate = (val: number) => {
    const validation = validateQuantity(val);

    if (inputRef.current) {
      inputRef.current.setCustomValidity(validation || "");
      inputRef.current.reportValidity();
      inputRef.current.value = String(val);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log(e);
    const val = Number(e.target.value);
    if (inputRef.current) {
      if (maxQuantity && val > maxQuantity) {
        inputRef.current.value = String(maxQuantity);
      } else if (!maxQuantity && val > DEFAULT_MAX) {
        inputRef.current.value = String(DEFAULT_MAX);
      }
    }
  };

  const handleIncrease = () => {
    const value = quantity + (factorQuantity ? minQuantity : 1);
    if (onUpdateQuantity) onUpdateQuantity(normalizeQuantity(value));
    handleInputUpdate(value);
  };

  const handleDecrease = () => {
    const value = quantity - (factorQuantity ? minQuantity : 1);
    if (onUpdateQuantity) onUpdateQuantity(normalizeQuantity(value));
    handleInputUpdate(value);
  };

  const isDisabled = (action: "increase" | "decrease") => {
    if (disabled) return true;
    if (action === "increase") return false;
    if (action === "decrease") return typeof quantity === "number" ? quantity <= 1 : false;
    return false;
  };

  return (
    <div className={classNames(styles.root, styles[type], styles[variants])}>
      <FixButton className={styles.button} variant="text" onClick={handleDecrease} disabled={isDisabled("decrease")}>
        <Icon src={minus} />
      </FixButton>

      <Input
        ref={inputRef}
        className={classNames(styles.input)}
        value={quantity.toString()}
        disabled={disabled}
        placeholder="1"
        type="number"
        onChange={handleChange}
        onInput={handleInput}
      />
      <FixButton className={styles.button} variant="text" onClick={handleIncrease} disabled={isDisabled("increase")}>
        <Icon src={plus} />
      </FixButton>
    </div>
  );
};

export default FixQuantity;
